import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Eye } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { useActiveTenant } from '../contexts/tenant'
import type { Product } from '@wsfull/shared'

type OutboundOrder = {
  id: string; orderNumber: string; channel: string; status: string
  customerName: string | null; packagesCount: number; createdAt: string
}
type ConsRow = {
  productId: string; sku: string; name: string
  fracionamento: number; reservaUnidades: number; total: number
}
type ItemRow = { productId: string; quantity: number }
type NfStatus = {
  nf: string; encontrada: boolean; jaExpedida: boolean; duplicada: boolean
  expedidaMaisDeUma: boolean; emMultiplasOndas: boolean
  orders: { pedido: string; serie: string | null; status: string; shippedAt: string | null; canal: string | null; ondas: string[] }[]
}

const statusLabel: Record<string, string> = {
  pending: 'Pendente', reserved: 'Reservado', picking: 'Separando',
  packed: 'Embalado', shipped: 'Expedido', cancelled: 'Cancelado',
}
const statusVariant: Record<string, 'default' | 'info' | 'warning' | 'success' | 'danger'> = {
  pending: 'default', reserved: 'info', picking: 'warning',
  packed: 'warning', shipped: 'success', cancelled: 'danger',
}
const nextStatus: Record<string, string> = { reserved: 'picking', picking: 'packed' }

export function OutboundPage() {
  const tenantId = useActiveTenant()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)

  // Reserva presa: a ordem manual reserva estoque ao ser criada, e a reserva
  // só morria em dois dos cinco caminhos de saída. O resto ficava vivo para
  // sempre, comendo o disponível — até recusar um pedido de 1 unidade com
  // centenas na prateleira. Corrigido na origem; isto limpa o que já ficou.
  const { data: presas, refetch: recontarPresas } = useQuery<{
    reservas: number; unidades: number; produtos: number; pedidos: number
  }>({
    queryKey: ['reservas-presas', tenantId],
    queryFn: async () => (await api.get('/stock/reservas-presas', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })
  const liberarPresas = useMutation({
    mutationFn: async () => (await api.post('/stock/reservas-presas/liberar', { tenantId })).data,
    onSuccess: async () => {
      await recontarPresas()
      await qc.invalidateQueries({ queryKey: ['availability', tenantId] })
      await qc.invalidateQueries({ queryKey: ['outbound', tenantId] })
    },
  })
  const [form, setForm] = useState({ orderNumber: '', customerName: '', channel: 'manual', packagesCount: '1', nfValue: '', modoEnvio: '', modoEnvioDetalhe: '' })
  const [items, setItems] = useState<ItemRow[]>([{ productId: '', quantity: 1 }])
  const [nfQuery, setNfQuery] = useState('')
  const [nfResult, setNfResult] = useState<NfStatus[] | null>(null)
  const nfCheck = useMutation({
    mutationFn: async () => {
      const nfs = nfQuery.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
      return (await api.post('/outbound/nf-status', { tenantId, nfs })).data as NfStatus[]
    },
    onSuccess: (r) => setNfResult(r),
  })
  const dupCheck = useMutation({
    mutationFn: async () => (await api.get('/outbound/duplicates', { params: { tenantId } })).data as { total: number; itens: NfStatus[] },
    onSuccess: (r) => { setNfResult(r.itens); setNfQuery('') },
  })

  const { data: orders = [], isLoading } = useQuery<OutboundOrder[]>({
    queryKey: ['outbound', tenantId],
    queryFn: async () => {
      if (!tenantId) return []
      return (await api.get('/outbound', { params: { tenantId } })).data
    },
    enabled: !!tenantId,
  })

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  // Disponibilidade por produto = fracionamento + armazém (paletes). O picking sai do
  // fracionamento; a reposição do armazém acontece na onda de separação.
  const { data: consolidated = [] } = useQuery<ConsRow[]>({
    queryKey: ['replenishment', 'consolidated', tenantId],
    queryFn: async () => (await api.get('/replenishment/consolidated', { params: { tenantId } })).data,
    enabled: !!tenantId && showForm,
  })

  const availForProduct = (productId: string) =>
    consolidated.find(c => c.productId === productId) ?? null

  const create = useMutation({
    mutationFn: async () => {
      await api.post('/outbound', {
        tenantId,
        orderNumber: form.orderNumber,
        customerName: form.customerName || undefined,
        channel: form.channel,
        packagesCount: parseInt(form.packagesCount) || 1,
        nfValue: form.nfValue ? parseFloat(form.nfValue) : undefined,
        modoEnvio: form.modoEnvio || undefined,
        modoEnvioDetalhe: form.modoEnvioDetalhe || undefined,
        items: items.filter(i => i.productId).map(i => ({
          productId: i.productId, quantity: i.quantity,
        })),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbound', tenantId] })
      qc.invalidateQueries({ queryKey: ['stock', 'balance', tenantId] })
      setShowForm(false)
      setForm({ orderNumber: '', customerName: '', channel: 'manual', packagesCount: '1', nfValue: '', modoEnvio: '', modoEnvioDetalhe: '' })
      setItems([{ productId: '', quantity: 1 }])
    },
  })

  const advance = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/outbound/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outbound', tenantId] }),
  })

  const ship = useMutation({
    mutationFn: (id: string) => api.post(`/outbound/${id}/ship`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbound', tenantId] })
      qc.invalidateQueries({ queryKey: ['stock', 'balance', tenantId] })
    },
  })

  const addItem = () => setItems(p => [...p, { productId: '', quantity: 1 }])
  const removeItem = (i: number) => setItems(p => p.filter((_, idx) => idx !== i))
  const setItemProduct = (i: number, productId: string) =>
    setItems(p => p.map((r, idx) => idx === i ? { ...r, productId } : r))
  const setItemQty = (i: number, v: number) =>
    setItems(p => p.map((r, idx) => idx === i ? { ...r, quantity: v } : r))

  // Ordem manual sai sem etiqueta de transportadora: o modo de envio é o que
  // substitui a etiqueta e o que a declaração de retirada vai carimbar.
  const canCreate = form.orderNumber && !!form.modoEnvio && items.some(i => i.productId)

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Ordens manuais</h2>
          <p className="text-sm text-gray-500">Ordens de saída criadas aqui (manual/cliente) · 500 mais recentes. Os pedidos do ERP ficam em <b>Pedidos</b>.</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(true)}><Plus className="h-4 w-4" /> Nova ordem</Button>
      </div>

      {!!presas?.reservas && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            {presas.reservas.toLocaleString('pt-BR')} reserva(s) presas seguram {presas.unidades.toLocaleString('pt-BR')} unidade(s)
          </p>
          <p className="mt-1 text-xs text-amber-800">
            São reservas de {presas.pedidos.toLocaleString('pt-BR')} pedido(s) que <b>já saíram do fluxo</b> (expedidos ou cancelados)
            e nunca foram soltas. Elas entram na conta do disponível — é por isso que uma ordem de 1 unidade pode ser
            recusada com centenas em estoque. Liberar não mexe no saldo: reserva não é estoque, é só a promessa de que
            ele está comprometido.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button size="sm" loading={liberarPresas.isPending} onClick={() => liberarPresas.mutate()}>
              Liberar reservas presas
            </Button>
            {liberarPresas.data && (
              <span className="text-xs text-green-800">
                {(liberarPresas.data as { liberadas: number; unidades: number }).liberadas.toLocaleString('pt-BR')} liberada(s) ·{' '}
                {(liberarPresas.data as { unidades: number }).unidades.toLocaleString('pt-BR')} unidade(s) de volta ao disponível
              </span>
            )}
            {liberarPresas.error && (
              <span className="text-xs text-red-700">
                {(liberarPresas.error as { response?: { data?: { title?: string } } })?.response?.data?.title ?? 'Falha ao liberar'}
              </span>
            )}
          </div>
        </div>
      )}

      <Modal title="Nova ordem de expedição" open={showForm} onClose={() => setShowForm(false)} size="lg">
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Número da ordem *" value={form.orderNumber}
              onChange={e => setForm({ ...form, orderNumber: e.target.value })} placeholder="PED-001" />
            <Input label="Nome do cliente" value={form.customerName}
              onChange={e => setForm({ ...form, customerName: e.target.value })} placeholder="João da Silva" />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Canal</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}>
                <option value="manual">Manual</option>
                <option value="bling">Bling</option>
                <option value="olist">Olist</option>
              </select>
            </div>
            <Input label="Volumes" type="number" min={1} value={form.packagesCount}
              onChange={e => setForm({ ...form, packagesCount: e.target.value })} />
            <Input label="Valor NF (R$)" type="number" value={form.nfValue}
              onChange={e => setForm({ ...form, nfValue: e.target.value })} placeholder="0,00" />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Modo de envio *</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.modoEnvio} onChange={e => setForm({ ...form, modoEnvio: e.target.value })}>
                <option value="">— escolha —</option>
                <option value="retirada">Retirada no balcão</option>
                <option value="motoboy">Motoboy / entrega própria</option>
                <option value="externa">Transportadora contratada por fora</option>
                <option value="etiqueta">Etiqueta anexada (vou anexar o PDF no pedido)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {form.modoEnvio === 'etiqueta'
                  ? 'Só entra em onda depois de anexar a etiqueta no pedido.'
                  : 'Sai sem etiqueta de transportadora: a onda imprime a declaração de retirada em 2 vias para assinar.'}
              </p>
            </div>
            <Input label={form.modoEnvio === 'retirada' ? 'Quem retira' : 'Transportadora / entregador'} value={form.modoEnvioDetalhe}
              onChange={e => setForm({ ...form, modoEnvioDetalhe: e.target.value })} placeholder={form.modoEnvio === 'retirada' ? 'Nome de quem vai buscar' : 'Ex.: Motoboy João, Jadlog…'} />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Itens *</label>
              <Button variant="ghost" size="sm" onClick={addItem}><Plus className="h-4 w-4" /> Adicionar</Button>
            </div>
            <div className="space-y-2">
              {items.map((item, i) => {
                const av = availForProduct(item.productId)
                const over = !!av && item.quantity > av.total
                return (
                  <div key={i} className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <select className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={item.productId} onChange={e => setItemProduct(i, e.target.value)}>
                        <option value="">Produto...</option>
                        {products.filter(p => p.active).map(p =>
                          <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                      </select>
                      {items.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeItem(i)}>✕</Button>}
                    </div>
                    {item.productId && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">Disponível</label>
                          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                            {av ? (
                              <>
                                <span className={`font-semibold ${av.total > 0 ? 'text-gray-900' : 'text-red-500'}`}>{av.total} un</span>
                                <span className="text-xs text-gray-400"> ({av.fracionamento} fracion. + {av.reservaUnidades} armazém)</span>
                              </>
                            ) : <span className="text-gray-400">—</span>}
                          </div>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">Quantidade</label>
                          <input type="number" min={1}
                            className={`w-full rounded-lg border px-3 py-2 text-sm text-right ${over ? 'border-red-400' : 'border-gray-200'}`}
                            value={item.quantity}
                            onChange={e => setItemQty(i, parseInt(e.target.value) || 1)} />
                          {over && <p className="mt-1 text-xs text-red-500">Acima do disponível ({av?.total} un)</p>}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {create.error && (
            <p className="text-sm text-red-600">
              {(create.error as any)?.response?.data?.detail ?? (create.error as any)?.response?.data?.title ?? (create.error as any)?.response?.data?.message ?? 'Erro ao criar'}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button loading={create.isPending} disabled={!canCreate} onClick={() => create.mutate()}>
              Criar ordem
            </Button>
          </div>
        </div>
      </Modal>

      <details className="rounded-xl border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-gray-800">Consultar expedição por NF — já enviada? duplicada? em 2+ ondas?</summary>
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">Cole os números das NFs (espaço, vírgula ou linha). Diz, por NF, os pedidos que casam, a série, o status e as ondas — para não expedir duas vezes.</p>
          <textarea value={nfQuery} onChange={e => setNfQuery(e.target.value)} rows={3}
            placeholder="85258, 85228, 85408…"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm" />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" loading={nfCheck.isPending} onClick={() => nfCheck.mutate()}>Consultar</Button>
            <Button size="sm" variant="secondary" loading={dupCheck.isPending} onClick={() => dupCheck.mutate()}>Ver todas as duplicatas</Button>
          </div>
          {nfResult && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-1 text-left">NF</th>
                    <th className="px-2 py-1 text-left">Situação</th>
                    <th className="px-2 py-1 text-left">Pedidos (série · status · ondas)</th>
                  </tr>
                </thead>
                <tbody>
                  {nfResult.map(r => {
                    const alert = r.jaExpedida || r.duplicada || r.emMultiplasOndas || r.expedidaMaisDeUma
                    return (
                      <tr key={r.nf} className={`border-b border-gray-100 ${alert ? 'bg-amber-50' : ''}`}>
                        <td className="px-2 py-1 font-mono">{r.nf}</td>
                        <td className="px-2 py-1">
                          {!r.encontrada ? <span className="text-gray-400">não encontrada</span> : (
                            <div className="flex flex-wrap gap-1">
                              {r.expedidaMaisDeUma && <Badge variant="danger">EXPEDIDA 2×</Badge>}
                              {r.jaExpedida && !r.expedidaMaisDeUma && <Badge variant="success">já expedida</Badge>}
                              {r.duplicada && <Badge variant="warning">duplicada</Badge>}
                              {r.emMultiplasOndas && <Badge variant="warning">2+ ondas</Badge>}
                              {!alert && <Badge variant="default">ok</Badge>}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1 text-gray-600">
                          {r.orders.map((o, i) => (
                            <div key={i}>
                              <span className="font-mono">{o.pedido}</span> · série {o.serie ?? '?'} · {o.status}
                              {o.shippedAt ? ` (${new Date(o.shippedAt).toLocaleDateString('pt-BR')})` : ''}
                              {o.ondas.length ? ` · ondas: ${o.ondas.join(', ')}` : ''}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </details>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : orders.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhuma ordem de expedição</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Ordem</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Canal</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Cliente</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Volumes</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((o) => (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium text-gray-900">{o.orderNumber}</td>
                      <td className="px-5 py-3 text-gray-600 capitalize">{o.channel}</td>
                      <td className="px-5 py-3 text-gray-600">{o.customerName ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-600">{o.packagesCount}</td>
                      <td className="px-5 py-3">
                        <Badge variant={statusVariant[o.status] ?? 'default'}>
                          {statusLabel[o.status] ?? o.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/outbound/${o.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {nextStatus[o.status] && (
                            <Button variant="secondary" size="sm" loading={advance.isPending}
                              onClick={() => advance.mutate({ id: o.id, status: nextStatus[o.status] })}>
                              → {statusLabel[nextStatus[o.status]]}
                            </Button>
                          )}
                          {o.status === 'packed' && (
                            <Button size="sm" loading={ship.isPending} onClick={() => ship.mutate(o.id)}>
                              Expedir
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
